/**
 * B-4v2 — pure freshness copy for the Today health-sync strip.
 *
 * Turns a last-synced LOCAL day ("YYYY-MM-DD") into calm, athlete-language
 * copy — no shame, no medical language, no raw values. "today" / "yesterday"
 * for the fresh cases; a short weekday+day for older syncs. Pure + injectable
 * `now` for testability (mirrors deriveSignals' device-local day framing).
 */

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

function localDayIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDay(iso: string): Date | null {
  if (!DAY_RE.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isFinite(dt.getTime()) ? dt : null;
}

/** Whole local-calendar-days between two "YYYY-MM-DD" days (a − b). */
function dayDelta(aIso: string, bIso: string): number | null {
  const a = parseDay(aIso);
  const b = parseDay(bIso);
  if (!a || !b) return null;
  return Math.round((a.getTime() - b.getTime()) / 86400000);
}

/**
 * Freshness phrase for a synced day, relative to `now` (device-local).
 *   today → "today" · yesterday → "yesterday" · older → "Mon 14".
 * Returns null for a malformed / future day (caller falls back).
 */
export function syncFreshnessLabel(lastSyncedDay: string | null | undefined, now: Date = new Date()): string | null {
  if (!lastSyncedDay) return null;
  const todayIso = localDayIso(now);
  const delta = dayDelta(todayIso, lastSyncedDay);
  if (delta == null || delta < 0) return null;
  if (delta === 0) return 'today';
  if (delta === 1) return 'yesterday';
  const d = parseDay(lastSyncedDay);
  if (!d) return null;
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  return `${weekday} ${d.getDate()}`;
}

/**
 * 2.0 status row (Part 1, frame 1k, string 30) — the last-synced day in the
 * long form the row's bold sentence prints: "Sunday, October 11". Fixed to
 * en-US like the app's other dated copy, so the sentence never mixes languages.
 * Null for a malformed day (caller falls back to "recently", as the strip does).
 */
export function syncLongDayLabel(lastSyncedDay: string | null | undefined): string | null {
  if (!lastSyncedDay) return null;
  const d = parseDay(lastSyncedDay);
  if (!d) return null;
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

/** True once a sync is >1 local day old — the strip reads it as stale. */
export function isSyncStale(lastSyncedDay: string | null | undefined, now: Date = new Date()): boolean {
  if (!lastSyncedDay) return true;
  const delta = dayDelta(localDayIso(now), lastSyncedDay);
  return delta == null || delta > 1;
}
